/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useEffect, useState } from 'react'
import { sendDelete, sendGet, sendPut } from '../../hooks/use-fetch'
import { Box, Button, Card, CircularProgress, Grid, Menu, MenuItem, Select, TextField, Typography, styled } from '@mui/material'
import { LargeCard } from '../Cards/Large'
import { MoreVert, DownloadForOfflineRounded, AttachMoney, DateRange, TaskAlt, CheckCircleRounded, UndoRounded, DeleteOutlineRounded } from '@mui/icons-material'
import { DownloadDocument, FormatCurrency, FormatDateValue } from '../../core/Utilities'
import { CheckList } from '../CheckList'
import { type SelectOption } from '../SelectWithLabel'
import { LabelWithToolTip } from '../Label/Index'
import { TextAreaWithLabel } from '../TextArea'
import { toast } from 'react-toastify'
import './document.css'
import { DocumentTarget } from '../../core/types'

export interface DocumentInfoProps {
  documentID?: number
  document: DocumentData | null
  childID: number
  disabled?: boolean
  onDelete?: (docId: number) => void
  onVerify?: (docId: number, data: DocumentUpdateModel) => Promise<void>
  onUnVerify?: (docId: number) => Promise<void>
  setVerifyMode: (docId: number, verifyMode: boolean) => Promise<void>
  verifyMode: boolean
  yearOptions: number[]

}
export interface DocumentData {
  documentID: number
  documentTypeID: number
  fileName: string
  childName: string
  incomeRelated: boolean
  documentTarget: DocumentTarget
  incomeAmount: number | null
  incomeYear: number | null
  verified: boolean
  verifiedDate: Date
  verifiedBy: string | null
  verificationNote: string | null
  inUse: boolean
  caregivers: DocumentCaregiver[]
}

export interface DocumentCaregiver {
  caregiverID: number
  caregiverName: string
  selected: boolean
}

export interface DocumentUpdateModel {
  documentID: number
  childID: number
  incomeAmount: number | null
  incomeYear: number | null
  verified: boolean
  verifiedBy: string | null
  verifiedDate: Date | null
  fileName: string | null
  verificationNote: string | null
  caregivers: number[]
}
const PersonDisplay = styled('div')({
  borderRadius: 10,
  backgroundColor: '#777777',
  padding: 5,
  display: 'inline-block',
  fontSize: '12px',
  whiteSpace: 'nowrap',
  color: 'white',
  marginRight: 5,
  marginBottom: 3
})
function FileExtension (name: string): string {
  if (document === null) {
    return ''
  } else {
    const parts = name.toUpperCase().split('.')
    let ext = ''
    if (parts.length > 1) {
      ext = parts[parts.length - 1]
    }
    if (ext === 'JPEG' || ext === 'HEIC') {
      return 'JPG'
    } else {
      return ext
    }
  }
}

function ExtensionColor (name: string): string {
  const ext = FileExtension(name)
  if (ext === 'PDF') {
    return '#7136BC'
  } else if (ext === 'JPG') {
    return '#BC3656'
  } else {
    return '#4DA4A4'
  }
}

const VerifyCard = styled(Card)({
  border: '1px solid #7136BC',
  borderRadius: 16,
  backgroundColor: '#E4D7F4',
  marginTop: 10,
  padding: 10
})

export function DocumentInfo (props: DocumentInfoProps): JSX.Element {
  const [document, setDocument] = useState<DocumentData | null>(props.document)
  const ExtensionDisplay = styled('div')({
    backgroundColor: ExtensionColor(document?.fileName ?? ''),
    padding: 5,
    borderRadius: 5,
    color: 'white',
    fontWeight: 'bold',
    height: 35,
    width: 35,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  })
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [selectedPeople, setSelectedPeople] = useState<Array<SelectOption<number>>>(
    props.document?.caregivers?.filter(c => c.selected).map(c => {
      const option: SelectOption<number> = {
        id: c.caregiverID,
        name: c.caregiverName
      }
      return option
    }) ?? [])
  const [refreshDate, setRefreshDate] = useState(new Date())
  useEffect(() => {
    setDocument(props.document)
  }, [props.document, props.document?.verified])
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, success } = await sendGet(`/Document/DocumentInfo?documentID=${props.documentID ?? 0}&childID=${props.childID}`, {})
      if (success) {
        const data = response as DocumentData
        let personList: Array<SelectOption<number>> = []
        if (data.documentTarget !== DocumentTarget.CHILD) {
          personList = data.caregivers.filter(c => c.selected).map((c) => {
            return { id: c.caregiverID, name: c.caregiverName }
          })
        }
        if (data.documentTarget !== DocumentTarget.ADULT) {
          personList.push({ id: -1, name: data.childName })
        }
        setSelectedPeople(personList)
        setDocument(data)
      }
    }
    if (props.document == null) {
      void fetchData()
    }
  }, [refreshDate])
  const showVerifyOptions = async (): Promise<void> => {
    if (document == null) { return }
    setMenuAnchor(null)
    await props.setVerifyMode(document.documentID, true)
  }
  const handleChange = (e: any): void => {
    if (document == null) { return }
    setDocument({ ...document, [e.target.name]: e.target.value })
  }

  const onCancel = async (): Promise<void> => {
    if (document == null) { return }
    await props.setVerifyMode(document.documentID, false)
  }

  const onConfirm = async (): Promise<void> => {
    if (document == null) { return }
    if (selectedPeople.length === 0) {
      toast.error('Please select at least one person this applies to')
    } else if (document.incomeRelated && (document.incomeAmount === null || document.incomeYear === null)) {
      toast.error('Please enter income amount and year')
    } else {
      const postData: DocumentUpdateModel = {
        documentID: document.documentID,
        childID: props.childID,
        incomeAmount: document.incomeAmount,
        incomeYear: document.incomeYear,
        verified: true,
        verifiedBy: document.verifiedBy,
        verifiedDate: document.verifiedDate,
        fileName: document.fileName,
        verificationNote: document.verificationNote,
        caregivers: selectedPeople.map((c) => c.id)
      }
      if (props.onVerify != null) {
        await props.onVerify(document.documentID, postData)
      } else {
        const { success } = await sendPut('/Document/UpdateDocument', postData)
        if (success) {
          setRefreshDate(new Date())
        }
      }
    }
  }

  const onFileLinkClick = (): void => {
    if (document == null) { return }
    const ext = FileExtension(document.fileName)
    if (ext === 'PDF' || ext === 'JPG' || ext === 'PNG') {
      const url = `${process.env.REACT_APP_SERVER_URL ?? ''}/Document/OpenFile/${document.documentID.toString()}`
      window.open(url, '_blank')
    } else {
      DownloadDocument(document.documentID)
    }
  }

  const unVerifyDocument = async (): Promise<void> => {
    if (document == null) { return }
    if (props.onUnVerify != null) {
      await props.onUnVerify(document.documentID)
    } else {
      const { response } = await sendPut(`/Document/UnverifyDocument/${document.documentID.toString()}`, {})
      if (response as boolean) {
        setRefreshDate(new Date())
      }
    }
  }

  const deleteDocument = async (): Promise<void> => {
    if (document == null) { return }
    if (props.onDelete != null) {
      props.onDelete(document?.documentID)
    } else {
      const { success } = await sendDelete(`/Document/Delete/${document?.documentID.toString()}`)
      if (success) {
        setRefreshDate(new Date())
      }
    }
  }
  let caregiverOptions: Array<SelectOption<number>> = []
  if (document != null) {
    if (document.documentTarget !== DocumentTarget.CHILD) {
      caregiverOptions = document.caregivers?.map((c) => {
        return { id: c.caregiverID, name: c.caregiverName }
      })
    }
    if (document.documentTarget !== DocumentTarget.ADULT) {
      caregiverOptions.push({ id: -1, name: document.childName })
    }
  }

  if (document === null) {
    return <CircularProgress />
  } else {
    const content = <>
      {!props.disabled && (

        <Menu
          open={menuAnchor !== null}
          onClose={() => { setMenuAnchor(null) }}
          anchorEl={menuAnchor}
        >
          <MenuItem key='Download' onClick={() => { DownloadDocument(document.documentID) }} >
            <DownloadForOfflineRounded className='mr-5' /> Download
          </MenuItem>
          {document.verified && !document.inUse && <MenuItem key='Unverify' onClick={async () => {
            await unVerifyDocument()
            setMenuAnchor(null)
          }}
          >
            <UndoRounded className='mr-5' /> Unverify
          </MenuItem>}
          {!document.verified && !document.inUse && !props.verifyMode && <MenuItem key='Delete' onClick={async () => {
            await deleteDocument()
            setMenuAnchor(null)
          }}
          >
            <DeleteOutlineRounded className='mr-5' /> Delete
          </MenuItem>
        }
        </Menu>
      )}
      <Grid container spacing={1} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'top', flexWrap: 'nowrap', width: '100%' }}>
        <Grid item> <ExtensionDisplay> {FileExtension(document.fileName)}</ExtensionDisplay></Grid>
        <Grid item>
          <a href='#' onClick={onFileLinkClick}>
            {document.fileName.substring(0, document.fileName.lastIndexOf('.'))}.{FileExtension(document.fileName)}
          </a>
          <div style={{ marginTop: '8px' }}>
            {document.documentTarget !== DocumentTarget.CHILD &&
              <>
                {document.caregivers?.map((c) => {
                  if (c.selected) {
                    return <PersonDisplay>{c.caregiverName}</PersonDisplay>
                  } else {
                    return null
                  }
                })}
              </>
            }
            {document.documentTarget !== DocumentTarget.ADULT &&
              <PersonDisplay>{document.childName}</PersonDisplay>
            }
          </div>
        </Grid>
        {!props.disabled && (
          <Grid item xs='auto' sx={{ marginLeft: 'auto' }}>
            <Button sx={{ color: '#969696' }} onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setMenuAnchor(event.currentTarget)
            }}
            >
              <MoreVert />
            </Button>
          </Grid>
        )}
      </Grid>
      {document.incomeRelated && document.verified && !props.verifyMode && <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'center', paddingTop: '5px' }}>
        <Grid item >
          <div style={{ display: 'inline-block', verticalAlign: 'middle' }}><AttachMoney fontSize='small' /></div>
          <span>{FormatCurrency(document.incomeAmount, true)}</span>
        </Grid>
        <Grid item >
          <div style={{ display: 'inline-block', verticalAlign: 'middle' }}><DateRange fontSize='small' /></div>
          <span> {document.incomeYear}</span>
        </Grid>
        {document.inUse && <Grid item >
          <div style={{ display: 'inline-block', verticalAlign: 'middle' }}><TaskAlt fontSize='small' /></div> In Use
        </Grid>
        }
      </Grid>
      }

      {document.verified && !props.verifyMode && <Grid container spacing={2} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', paddingTop: '5px' }}>
        <Grid item >
          <CheckCircleRounded className='mr-5' htmlColor='#02AED9' />
        </Grid>
        <Grid item sx={{ fontSize: '12px', color: '#969696' }}>
          <div>Verified {FormatDateValue(document.verifiedDate)} by {document.verifiedBy} </div>
          <div> {document.verificationNote} </div>
        </Grid>
      </Grid>
      }
      {!props.disabled && !props.verifyMode && !document.inUse &&
        <Button variant='text' data-testid='reviewVerification' onClick={showVerifyOptions} >{(document.verified ? 'Review Verification' : 'Verify Document')}</Button>
      }

      {props.verifyMode && <VerifyCard>
        <Typography variant='h6'>Verify Information</Typography>
        <CheckList label='Who does this document apply to?' items={caregiverOptions} selectedItems={selectedPeople} setSelectedItems={setSelectedPeople} />
        {document.incomeRelated && <div>
          <LabelWithToolTip
            labelText={'What is the income defined in this document?'}
          />
          <Box sx={{ width: '100%' }}>
            <span>$ </span>
            <TextField
              name='incomeAmount'
              id='incomeAmount'
              type='number'
              value={document.incomeAmount}
              onChange={handleChange}
              required={true}
              sx={{ width: '130px', backgroundColor: 'white', marginRight: 2 }}
            />
            <Select
              name='incomeYear'
              value={document.incomeYear ?? ''}
              onChange={handleChange}
              required={true}
              variant="outlined"
              sx={{ backgroundColor: 'white', width: '130px' }}
            >
              {props.yearOptions.map((opt) => (
                <MenuItem key={opt.toString()} value={opt.toString()} role="option">
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </div>
        }
        <TextAreaWithLabel
          label='Verify the contents of this document'
          name='verificationNote'
          onChange={handleChange}
          value={document.verificationNote}
        />
        <div style={{ marginTop: 10 }}>
          <Button
            data-testid='cancelButton'
            variant='outlined'
            color='inherit'
            sx={{ fontWeight: 'bold', backgroundColor: 'white' }}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            data-testid='updateButton'
            color='secondary'
            variant='contained'
            onClick={onConfirm}
            sx={{ float: 'right' }}
          >
            Verify
          </Button>
        </div>
      </VerifyCard>}
    </>

    return <LargeCard content={content} contentClassName={document.verified ? 'verified-document' : ''} className='document-display' />
  }
}
